import React from "react"
import {useTranslation} from "react-i18next"
import {UsersIcon} from "@heroicons/react/16/solid"

import {OptionsToggleBase} from "../../../../components/fields/OptionsToggle.tsx"
import {DropdownContext} from "../../../../components/formElements/Dropdown/context.ts"
import {DropdownBase} from "../../../../components/formElements/Dropdown/Dropdown.tsx"
import {TOption} from "../../../../components/formElements/Dropdown/types.ts"
import {useSalesAssistantSalesPeopleSelectBoxQuery} from "../../../../queries/salesAssistant.ts"
import {ASalesAssistantSalesPersonBasic} from "../../../../services/types.generated.ts"
import {EMPTY_ARRAY} from "../../../../utils/index.ts"
import {useSalesAssistantSalesPersonFilter} from "../../hooks.ts"

// The selector is always "selected" so the OptionsToggleBase renders the single blue pill (matching
// the PeriodToggle look in DashboardSaaSCompany), with the dropdown living inside it.
const SELECTED = "selected"
const noop = () => undefined

export const SalesPersonSelect: React.FC = () => {
  const {t} = useTranslation()
  const [value, setValue] = useSalesAssistantSalesPersonFilter()

  const {data} = useSalesAssistantSalesPeopleSelectBoxQuery()

  const options = React.useMemo<Array<TOption<string>>>(() => {
    const salesPeople = (data?.data.sales_people ?? EMPTY_ARRAY) as ASalesAssistantSalesPersonBasic[]

    return salesPeople.map(salesPerson => ({
      value: salesPerson.external_id,
      title: [salesPerson.first_name, salesPerson.last_name].filter(Boolean).join(" "),
      key: salesPerson.external_id,
    }))
  }, [data?.data.sales_people])

  const toggleOptions = React.useMemo<Array<TOption<string>>>(
    () => [
      {
        key: SELECTED,
        value: SELECTED,
        title: (
          <div className={"min-w-40"}>
            <DropdownBase
              options={options}
              value={value}
              onChange={setValue}
              clearable
              placeholder={t("SalesAssistant_Settings_SalesPersonFilter_All")}
              name={"salesPersonPicker"}
              listWidth={"list-auto"}
              mainButtonClassName={"text-xs input-border-ghost cursor-pointer"}
              caretClassName={"hover:bg-transparent"}
              SelectedValue={SelectedValue}
            />
          </div>
        ),
      },
    ],
    [options, value, setValue, t]
  )

  return <OptionsToggleBase options={toggleOptions} value={SELECTED} onChange={noop} />
}

const SelectedValue: React.FC = () => {
  const {value, multiple, placeholder} = DropdownContext.useContext()

  if (multiple) {
    return null
  }

  return (
    <div className={"flex items-center gap-2 overflow-hidden"}>
      <UsersIcon className={"-mt-0.5 size-5 shrink-0"} />
      <span className={"grow truncate text-center"}>{value?.title ?? placeholder}</span>
    </div>
  )
}
